/**
 * "Change today's items" — the daily-special swap flow.
 *
 * Row-anchored: tap a line to search a replacement; the new item inherits
 * that line's price / type / target, so the shell's pricing cannot be lost.
 * Save sends ONE PUT with BOTH line arrays (an omitted array would wipe
 * that side's lines server-side); attributes are echoed back verbatim.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BottomSheet, Icon } from './list';
import { getListProduct } from '../services/items.services';
import {
  getDetailsPromotion,
  pushPromotionsToTills,
  updatePromotion,
} from '../services/promotion.service';
import { showToast } from '../services/toast.service';
import { showApiError } from '../services/apiError';
import { useResponsive } from '../hook/useResponsive';

const TEAL = '#0F7679';
const INK = '#222525';
const MUTED = '#6A7878';
const BORDER = '#DBE4E4';
const GOOD = '#0E8B53';
const RED = '#BA0E13';

interface SwapLine {
  key: string;
  productId: string;
  productName: string;
  value: number | string;
  targetTrigger: number;
  discountType: number;
  replacement?: { productId: string; productName: string } | null;
  removed: boolean;
  isNew: boolean;
}

interface SearchResult {
  productId: string;
  productName: string;
  groupName: string;
  sku: string;
  uomName: string;
}

interface PromotionSwapSheetProps {
  open: boolean;
  promotionId: string;
  language: any;
  onClose: () => void;
  /** Called after a successful save so the list can reload. */
  onSaved: () => void;
}

/** Faithful numeric pass-through: 0 stays 0, unparseable falls back. */
const numberOr = (raw: any, fallback: number | null) => {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const PromotionSwapSheet: React.FC<PromotionSwapSheetProps> = ({
  open,
  promotionId,
  language,
  onClose,
  onSaved,
}) => {
  const { isMobile } = useResponsive();
  const t = (key: string, fallback: string) =>
    language && typeof language[key] === 'string' && language[key] !== ''
      ? language[key]
      : fallback;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [promo, setPromo] = useState<any>(null);
  const [lines, setLines] = useState<SwapLine[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<any>(null);

  useEffect(() => {
    if (!open || !promotionId) return;
    let cancelled = false;
    setLoading(true);
    setPromo(null);
    setLines([]);
    setActiveKey(null);
    setSearch('');
    setResults([]);
    (async () => {
      try {
        const res: any = await getDetailsPromotion(promotionId);
        if (cancelled) return;
        const data = res?.data?.data;
        setPromo(data);
        setLines(
          (data?.promotionProducts || []).map((row: any, idx: number) => ({
            key: `${row.productId}-${idx}`,
            productId: row.productId,
            productName:
              row.product?.productName || t('Unknown item', 'Unknown item'),
            value: numberOr(row.value, 0) as number,
            targetTrigger: numberOr(row.targetTrigger, 1) as number,
            discountType: numberOr(row.discountType, 1) as number,
            replacement: null,
            removed: false,
            isNew: false,
          })),
        );
      } catch (error) {
        console.error('Error loading promotion for swap:', error);
        if (!cancelled) {
          showApiError(error, {
            language,
            titleFallback: t('Unable to load the promotion', 'Unable to load the promotion'),
          });
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, promotionId]);

  // Live product search, debounced; same picker data as the Add products modal.
  const searchSeqRef = useRef(0);
  useEffect(() => {
    if (activeKey === null) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const seq = ++searchSeqRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        setSearching(true);
        const res: any = await getListProduct(
          1,
          20,
          search,
          localStorage.getItem('companyId') || '',
        );
        if (seq !== searchSeqRef.current) return; // a newer query is in flight
        const fetched = res?.data?.productWithCompany || [];
        setResults(
          fetched.map((value: any) => ({
            productId: value.id,
            productName: value.productName,
            groupName: value.productGroup?.groupName || '',
            sku: value.sku || '',
            uomName:
              value.productUOMs?.find((v: any) => v.isBase)?.uom?.uomName || '',
          })),
        );
      } catch (error) {
        console.error('Error searching products:', error);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search, activeKey]);

  const survivors = lines.filter(
    (line) => !line.removed && (line.productId || line.replacement),
  );
  const changedCount = lines.filter(
    (line) =>
      (line.removed || line.replacement || line.isNew) &&
      (line.productId || line.replacement),
  ).length;

  const takenProductIds = useMemo(() => {
    const ids = new Set<string>();
    for (const line of lines) {
      if (line.removed) continue;
      ids.add(line.replacement ? line.replacement.productId : line.productId);
    }
    return ids;
  }, [lines]);

  const openSearchFor = (key: string) => {
    setActiveKey((prev) => (prev === key ? null : key));
    setSearch('');
    setResults([]);
    requestAnimationFrame(() => {
      document
        .getElementById(`swap-line-${key}`)
        ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  };

  const updateLineValue = (key: string, raw: string) => {
    setLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, value: raw } : line)),
    );
  };

  const pickProduct = (key: string, product: SearchResult) => {
    if (takenProductIds.has(product.productId)) {
      showToast({
        message: t(
          'This item is already on the promotion',
          'This item is already on the promotion',
        ),
        type: 'warning',
        duration: 5000,
      });
      return;
    }
    setLines((prev) =>
      prev.map((line) => {
        if (line.key !== key) return line;
        if (line.isNew) {
          return {
            ...line,
            productId: product.productId,
            productName: product.productName,
          };
        }
        return {
          ...line,
          replacement: {
            productId: product.productId,
            productName: product.productName,
          },
        };
      }),
    );
    setActiveKey(null);
  };

  const undoLine = (key: string) => {
    const target = lines.find((line) => line.key === key);
    if (!target) return;
    if (!target.isNew) {
      // Restoring this line's original product must not collide with a swap
      // another line staged in the meantime.
      const restoredId = target.productId;
      const claimedElsewhere = lines.some(
        (line) =>
          line.key !== key &&
          !line.removed &&
          (line.replacement ? line.replacement.productId : line.productId) ===
            restoredId,
      );
      if (claimedElsewhere) {
        showToast({
          message: t(
            'This item is already on the promotion',
            'This item is already on the promotion',
          ),
          type: 'warning',
          duration: 5000,
        });
        return;
      }
    }
    setLines((prev) =>
      prev
        .filter((line) => !(line.key === key && line.isNew))
        .map((line) =>
          line.key === key
            ? { ...line, replacement: null, removed: false }
            : line,
        ),
    );
  };

  const removeLine = (key: string) => {
    if (survivors.length <= 1) {
      showToast({
        message: t(
          'A promotion needs at least one item — deactivate it instead',
          'A promotion needs at least one item — deactivate it instead',
        ),
        type: 'warning',
        duration: 5000,
      });
      return;
    }
    setLines((prev) =>
      prev.map((line) =>
        line.key === key ? { ...line, removed: true } : line,
      ),
    );
  };

  const addLine = () => {
    // A brand-new line inherits the promo's most common price rule.
    const counts = new Map<string, number>();
    for (const line of survivors) {
      const sig = `${line.value}|${line.discountType}|${line.targetTrigger}`;
      counts.set(sig, (counts.get(sig) ?? 0) + 1);
    }
    const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
    const [value, discountType, targetTrigger] = top
      ? top.split('|').map(Number)
      : [0, 1, 1];
    const key = `new-${Date.now()}`;
    setLines((prev) => [
      ...prev,
      {
        key,
        productId: '',
        productName: '',
        value,
        targetTrigger,
        discountType,
        replacement: null,
        removed: false,
        isNew: true,
      },
    ]);
    openSearchFor(key);
  };

  const save = async (push: boolean) => {
    const finalLines = lines.filter(
      (line) => !line.removed && (line.productId || line.replacement),
    );
    if (finalLines.length === 0) {
      showToast({
        message: t(
          'A promotion needs at least one item — deactivate it instead',
          'A promotion needs at least one item — deactivate it instead',
        ),
        type: 'warning',
        duration: 5000,
      });
      return;
    }
    // One resolved productId per line, whatever path staged it.
    const seen = new Set<string>();
    const productLines = finalLines.filter((line) => {
      const resolvedId = line.replacement
        ? line.replacement.productId
        : line.productId;
      if (seen.has(resolvedId)) return false;
      seen.add(resolvedId);
      return true;
    });
    setSaving(true);
    let savedOk = false;
    try {
      const payload = {
        promotionProduct: productLines.map((line) => ({
          productId: line.replacement
            ? line.replacement.productId
            : line.productId,
          targetTrigger: numberOr(line.targetTrigger, 1),
          value: numberOr(line.value, 0),
          discountType: numberOr(line.discountType, 1),
        })),
        // Echo attributes faithfully: an omitted array is a server-side wipe,
        // and untouched rows must keep their stored values (0 and null included).
        promotionAttribute: (promo?.promotionAttributes || []).map(
          (row: any) => ({
            attributeId: row.attributeId,
            targetTrigger: numberOr(row.targetTrigger, 1),
            value: numberOr(row.value, 0),
            discountType: numberOr(row.discountType, null),
          }),
        ),
      };
      await updatePromotion(promotionId, payload);
      savedOk = true;
      if (push) {
        await pushPromotionsToTills();
      }
      showToast({
        message: push
          ? t(
              'Saved and pushed — tills apply it at their next sync',
              'Saved and pushed — tills apply it at their next sync',
            )
          : t(
              'Saved — push to tills to publish today',
              'Saved — push to tills to publish today',
            ),
        type: 'success',
        duration: 5000,
      });
      onSaved();
      onClose();
    } catch (error: any) {
      console.error('Error saving swap:', error);
      if (savedOk) {
        // The save landed; only the push failed. Say so — tills will still
        // pick the change up at their next sync.
        showToast({
          message: t(
            'Saved, but the push failed — tills apply it at their next sync',
            'Saved, but the push failed — tills apply it at their next sync',
          ),
          type: 'warning',
          duration: 5000,
        });
        onSaved();
        onClose();
      } else {
        showApiError(error, {
          language,
          titleFallback: t('Unable to save the change', 'Unable to save the change'),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const lineRow = (line: SwapLine) => {
    const staged = !!line.replacement;
    const showingSearch = activeKey === line.key;
    const borderColor = line.removed
      ? BORDER
      : staged || line.isNew
        ? TEAL
        : BORDER;
    return (
      <div key={line.key} id={`swap-line-${line.key}`}>
        <div
          className="font-inter"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            border: `1px ${line.removed ? 'dashed' : 'solid'} ${borderColor}`,
            borderRadius: '12px',
            padding: '11px 12px',
            marginTop: '8px',
            opacity: line.removed ? 0.55 : 1,
            background: staged ? '#F2F9F6' : '#FFFFFF',
            cursor: line.removed ? 'default' : 'pointer',
          }}
          onClick={() => {
            if (!line.removed) openSearchFor(line.key);
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: INK }}>
              {line.isNew && !line.productName ? (
                t('New item — pick a product', 'New item — pick a product')
              ) : staged ? (
                <>
                  {line.productName}{' '}
                  <span style={{ color: MUTED, fontWeight: 500 }}>→</span>{' '}
                  {line.replacement!.productName}
                </>
              ) : (
                line.productName
              )}
            </div>
            <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>
              {line.removed
                ? t('will be removed', 'will be removed')
                : staged
                  ? t(
                      'swapped — keeps this line’s price',
                      'swapped — keeps this line’s price',
                    )
                  : t('tap to swap', 'tap to swap')}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flex: 'none',
            }}
          >
            {line.isNew && line.productName ? (
              <input
                inputMode="decimal"
                value={String(line.value ?? '')}
                placeholder="0.00"
                onClick={(event) => event.stopPropagation()}
                onChange={(event) =>
                  updateLineValue(line.key, event.target.value)
                }
                className="font-inter"
                style={{
                  width: '84px',
                  height: '40px',
                  padding: '0 10px',
                  border: `1px solid ${TEAL}`,
                  borderRadius: '10px',
                  textAlign: 'right',
                  fontFamily: 'ui-monospace, Menlo, monospace',
                  fontSize: isMobile ? '16px' : '14px',
                  fontWeight: 700,
                  color: INK,
                  fontVariantNumeric: 'tabular-nums',
                  outline: 'none',
                  background: '#FFFFFF',
                }}
              />
            ) : (
              <span
                style={{
                  fontFamily: 'ui-monospace, Menlo, monospace',
                  fontSize: '13.5px',
                  fontWeight: 700,
                  color: staged ? GOOD : INK,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {Number(line.value).toFixed(2)}
              </span>
            )}
            {(staged || line.removed || line.isNew) && (
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: MUTED,
                  cursor: 'pointer',
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  undoLine(line.key);
                }}
              >
                {t('Undo', 'Undo')}
              </span>
            )}
            {!staged && !line.removed && !line.isNew && (
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: RED,
                  cursor: 'pointer',
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  removeLine(line.key);
                }}
              >
                {t('Remove', 'Remove')}
              </span>
            )}
          </div>
        </div>
        {showingSearch && !line.removed && (
          <div style={{ margin: '8px 0 4px' }}>
            <div
              className="font-inter"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                height: '40px',
                padding: '0 12px',
                border: '1px solid #C9D7D8',
                borderRadius: '12px',
                background: '#FFFFFF',
              }}
            >
              <Icon name="search" size={16} />
              <input
                autoFocus
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('Search products', 'Search products')}
                className="font-inter placeholder:text-placeholder outline-none focus-none bg-transparent w-full"
                style={{
                  fontSize: isMobile ? '16px' : '14px',
                  color: INK,
                  border: 'none',
                }}
              />
            </div>
            {searching && (
              <p
                className="font-inter"
                style={{ fontSize: '12.5px', color: MUTED, padding: '8px 4px' }}
              >
                {t('Searching…', 'Searching…')}
              </p>
            )}
            {!searching &&
              results
                .filter((result) => !takenProductIds.has(result.productId))
                .slice(0, 6)
                .map((result) => (
                  <div
                    key={result.productId}
                    className="font-inter"
                    onClick={() => pickProduct(line.key, result)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '10px',
                      border: `1px solid ${BORDER}`,
                      borderRadius: '12px',
                      padding: '10px 12px',
                      marginTop: '8px',
                      cursor: 'pointer',
                      background: '#FFFFFF',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '14px', color: INK }}>
                        {result.productName}
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: MUTED,
                          marginTop: '2px',
                        }}
                      >
                        {[result.groupName, result.sku, result.uomName]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '6px',
                        border: '1px solid #C9D7D8',
                        flex: 'none',
                      }}
                    />
                  </div>
                ))}
          </div>
        )}
      </div>
    );
  };

  const titleBlock = (
    <>
      <p
        className="font-inter"
        style={{ fontSize: '17px', fontWeight: 700, color: INK }}
      >
        {t("Change today's items", "Change today's items")}
      </p>
      <p
        className="font-inter"
        style={{ fontSize: '12.5px', color: MUTED, marginTop: '2px' }}
      >
        {promo?.promoName || ''}
      </p>
    </>
  );

  const bodyBlock = loading ? (
    <p
      className="font-inter"
      style={{ fontSize: '13px', color: MUTED, padding: '24px 0' }}
    >
      {t('Loading…', 'Loading…')}
    </p>
  ) : (
    <>
      {lines.map((line) => lineRow(line))}

      <div
        className="font-inter"
        onClick={addLine}
        style={{
          border: `1px dashed ${BORDER}`,
          borderRadius: '12px',
          padding: '11px 12px',
          marginTop: '8px',
          textAlign: 'center',
          color: TEAL,
          fontWeight: 600,
          fontSize: '13px',
          cursor: 'pointer',
        }}
      >
        ＋ {t('Add another item', 'Add another item')}
      </div>

      <p
        className="font-inter"
        style={{
          fontSize: '12px',
          color: MUTED,
          background: '#F8FAFA',
          borderRadius: '10px',
          padding: '8px 12px',
          marginTop: '12px',
        }}
      >
        {changedCount > 0
          ? `${changedCount} ${t('changing', 'changing')} · ${survivors.length} ${t('items after save', 'items after save')}`
          : t(
              'Tap an item to swap it for today',
              'Tap an item to swap it for today',
            )}
      </p>
    </>
  );

  const actionsBlock = loading ? null : (
    <>
      <div style={{ display: 'flex', gap: '10px' }}>
        <div
          className="font-inter"
          onClick={() => !saving && save(false)}
          style={{
            flex: 1,
            textAlign: 'center',
            fontSize: '13.5px',
            fontWeight: 600,
            borderRadius: '30px',
            padding: '12px 10px',
            border: `1px solid #C9D7D8`,
            color: INK,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {t('Save only', 'Save only')}
        </div>
        <div
          className="font-inter"
          onClick={() => !saving && save(true)}
          style={{
            flex: 1,
            textAlign: 'center',
            fontSize: '13.5px',
            fontWeight: 600,
            borderRadius: '30px',
            padding: '12px 10px',
            background: TEAL,
            color: '#FFFFFF',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving
            ? t('Saving…', 'Saving…')
            : t('Save & push to tills', 'Save & push to tills')}
        </div>
      </div>
      <p
        className="font-inter"
        style={{
          fontSize: '11px',
          color: MUTED,
          textAlign: 'center',
          marginTop: '10px',
          lineHeight: 1.5,
        }}
      >
        {t(
          'Push refreshes the sync snapshot now. Each till still applies it at its next start-of-day or manual sync.',
          'Push refreshes the sync snapshot now. Each till still applies it at its next start-of-day or manual sync.',
        )}
      </p>
    </>
  );

  // Phones get a full-screen takeover: a bottom sheet anchors the search
  // results exactly where the keyboard lands. Desktop keeps the sheet.
  if (isMobile) {
    if (!open) return null;
    return (
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 200,
          background: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '14px 16px 12px',
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <div style={{ minWidth: 0 }}>{titleBlock}</div>
          <div
            onClick={() => !saving && onClose()}
            aria-label={t('Close', 'Close')}
            style={{
              flex: 'none',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <Icon name="close" size={18} />
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 16px 24px' }}>
          {bodyBlock}
        </div>
        {actionsBlock && (
          <div
            style={{
              padding: '12px 16px calc(12px + env(safe-area-inset-bottom))',
              borderTop: `1px solid ${BORDER}`,
              background: '#FFFFFF',
            }}
          >
            {actionsBlock}
          </div>
        )}
      </div>
    );
  }

  return (
    <BottomSheet open={open} onClose={saving ? () => {} : onClose}>
      <div style={{ padding: '6px 20px 18px' }}>
        {titleBlock}
        {bodyBlock}
        {actionsBlock && (
          <div style={{ marginTop: '14px' }}>{actionsBlock}</div>
        )}
      </div>
    </BottomSheet>
  );
};

export default PromotionSwapSheet;
